import { useEffect, useRef } from "react";

// 3D-ish floating polaroids canvas — with depth & parallax. Replaces particles.
// Cream paper aesthetic: drifts of hand-drawn heart outlines instead of filled particles.
export default function HeartCanvas() {
    const canvasRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0, active: false });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf;
        const dpr = window.devicePixelRatio || 1;
        let w = (canvas.width = canvas.offsetWidth * dpr);
        let h = (canvas.height = canvas.offsetHeight * dpr);

        const onResize = () => {
            w = canvas.width = canvas.offsetWidth * dpr;
            h = canvas.height = canvas.offsetHeight * dpr;
        };
        window.addEventListener("resize", onResize);

        const onMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouseRef.current.x = (e.clientX - rect.left) * dpr;
            mouseRef.current.y = (e.clientY - rect.top) * dpr;
            mouseRef.current.active = true;
        };
        canvas.addEventListener("mousemove", onMove);
        canvas.addEventListener("mouseleave", () => {
            mouseRef.current.active = false;
        });

        // Sparse hand-drawn outline hearts
        const COUNT = 26;
        const items = Array.from({ length: COUNT }).map(() => ({
            x: Math.random() * w,
            y: Math.random() * h,
            z: Math.random() * 0.7 + 0.3,
            vx: (Math.random() - 0.5) * 0.25,
            vy: (Math.random() - 0.5) * 0.2 - 0.08,
            r: Math.random() * 14 + 10,
            rot: Math.random() * Math.PI * 2,
            rotSpeed: (Math.random() - 0.5) * 0.006,
            filled: Math.random() < 0.18,
            color: Math.random() < 0.7 ? "#ff2d6b" : "#5b0a1a",
        }));

        function drawHeart(x, y, size, rot, color, filled, alpha) {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(rot);
            ctx.scale(size / 16, size / 16);
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.moveTo(0, -4);
            ctx.bezierCurveTo(0, -7, -5, -8, -7, -4);
            ctx.bezierCurveTo(-10, 1, -3, 6, 0, 9);
            ctx.bezierCurveTo(3, 6, 10, 1, 7, -4);
            ctx.bezierCurveTo(5, -8, 0, -7, 0, -4);
            ctx.closePath();
            if (filled) {
                ctx.fillStyle = color;
                ctx.fill();
            } else {
                ctx.lineWidth = 1.2;
                ctx.strokeStyle = color;
                ctx.stroke();
            }
            ctx.restore();
        }

        function loop() {
            ctx.clearRect(0, 0, w, h);

            items.forEach((p) => {
                p.x += p.vx * p.z;
                p.y += p.vy * p.z;
                p.rot += p.rotSpeed;

                if (mouseRef.current.active) {
                    const dx = mouseRef.current.x - p.x;
                    const dy = mouseRef.current.y - p.y;
                    const dist = Math.hypot(dx, dy);
                    if (dist < 200 * dpr) {
                        p.x -= (dx / dist) * 0.5 * p.z;
                        p.y -= (dy / dist) * 0.5 * p.z;
                    }
                }

                if (p.x < -40) p.x = w + 40;
                if (p.x > w + 40) p.x = -40;
                if (p.y < -40) p.y = h + 40;
                if (p.y > h + 40) p.y = -40;

                drawHeart(
                    p.x,
                    p.y,
                    p.r * dpr,
                    p.rot,
                    p.color,
                    p.filled,
                    0.25 + p.z * 0.4,
                );
            });

            raf = requestAnimationFrame(loop);
        }
        loop();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
            canvas.removeEventListener("mousemove", onMove);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            data-testid="heart-canvas"
            aria-hidden="true"
        />
    );
}
